import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, TrendingUp } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchCountryGrowth, type CountryRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { EmptyState, ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A11.20 — Davlat bo'yicha o'sish. `/admin/countries` dagi ro'yxatdan-o'tganlar
 * soni bo'yicha reyting (proporsional gorizontal chiziqlar). Alohida endpoint yo'q.
 */
export function CountryGrowth() {
  const t = useT()
  const { can } = useAuth()
  const [rows, setRows] = useState<CountryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchCountryGrowth(ac.signal)
      .then((r) => {
        if (alive) setRows(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (!can(PERMISSIONS.COUNTRIES_VIEW)) return <PermissionDenied permission={PERMISSIONS.COUNTRIES_VIEW} />

  const top = rows.slice(0, 25)
  const max = top.length ? top[0]!.users : 0

  return (
    <>
      <PageHeader
        title={t.geo.growth}
        screenId="A11.20"
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.nav.geo}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-3xl space-y-2">
            {Array.from({ length: 10 }, (_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !top.length ? (
          <EmptyState icon={<TrendingUp size={28} strokeWidth={1.5} />} title={t.states.emptyTitle} />
        ) : (
          <div className="max-w-3xl space-y-1.5">
            {top.map((c) => (
              <div key={c.code} className="flex items-center gap-3">
                <Link
                  to={`/geo/${c.code}`}
                  className="flex w-44 shrink-0 items-center gap-1.5 truncate text-sm text-secondary hover:text-primary"
                >
                  <span>{c.flag || '🌐'}</span>
                  <span className="truncate">{c.name}</span>
                </Link>
                <div className="relative h-5 flex-1 overflow-hidden rounded bg-surface-2">
                  <div
                    className="h-full rounded bg-accent/70"
                    style={{ width: max ? `${Math.max(2, (c.users / max) * 100)}%` : '0%' }}
                  />
                </div>
                <span className="w-16 shrink-0 text-right font-mono text-sm tabular text-primary">
                  {c.users.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
